import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

export type JobStatus = 'active' | 'draft' | 'scheduled' | 'paused' | 'closed';

export interface JobItem {
  id?: number;
  assessment_id?: string;
  title: string;
  published_at: string | null;
  no_of_visits: number;
  no_of_applications: number;
  status: JobStatus;
  assessment_link?: string;
  deadline?: string | null;
}

interface ManageJobsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: JobItem[];
}

interface UseManageJobsParams {
  search?: string;
  status?: JobStatus | 'all';
  page?: number;
  pageSize?: number;
}

/**
 * Hook for fetching manage jobs table data with search, status filters, and pagination
 */
export const useManageJobs = ({ search, status, page = 1, pageSize = 8 }: UseManageJobsParams = {}) => {
  const { openToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['manageJobs', search, status, page, pageSize],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
      };
      
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      if (status && status !== 'all') {
        params.status = status;
      }

      const response = await apiClient.get<ManageJobsResponse>('/assessment/jobs/', {
        params,
      });
      return response.data;
    },
    retry: 1,
  });

  if (error) {
    const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch jobs';
    openToast({
      show: true,
      heading: "Error",
      text: errorMessage,
      type: "error",
      timeOut: 5000,
    });
  }

  return {
    jobsData: data,
    jobs: data?.results || [],
    totalCount: data?.count || 0,
    totalPages: Math.ceil((data?.count || 0) / pageSize),
    nextPage: data?.next,
    previousPage: data?.previous,
    isLoading,
    error,
    refetch,
  };
};
